import React, { useState, useRef, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { useVoiceMode } from '../../utils/voiceMode';
import { useAuth } from '../../context/AuthContext';
import { getMemberCards } from '../../services/api';
import { askAssistant, HAS_AI_KEY } from '../../services/ai';

// Desktop Ask AI — the SAME assistant the mobile /ask page uses (same askAssistant
// → /ai/ask API, same conversation storage), rendered inside the admin layout so
// the sidebar stays. No AI/API/webhook changes; this is a desktop presentation only.
const SUGGESTED = [
  { icon: '📋', text: 'What do I need for my jobs tomorrow?' },
  { icon: '📍', text: 'Where are my job sites?' },
  { icon: '👷', text: 'Who else is on my crew?' },
  { icon: '✅', text: "What's left to do on my jobs?" },
  { icon: '📝', text: 'Any notes from Ryan on my jobs?' },
  { icon: '⚡', text: 'What materials do I need?' },
];

function MessageText({ text }) {
  const lines = String(text || '').split('\n');
  return (
    <div>
      {lines.map((line, i) => {
        if (!line.trim()) return <br key={i} />;
        const parts = line.split(/\*\*(.*?)\*\*/g);
        const rendered = parts.map((part, j) => (j % 2 === 1 ? <strong key={j}>{part}</strong> : part));
        if (line.trimStart().startsWith('•') || line.trimStart().startsWith('-')) {
          return <div key={i} style={{ paddingLeft: 14, position: 'relative', margin: '2px 0' }}>{rendered}</div>;
        }
        return <div key={i} style={{ margin: '2px 0' }}>{rendered}</div>;
      })}
    </div>
  );
}

export default function AdminAskAI() {
  const { worker } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [listening, setListening] = useState(false);
  // Hands-free: talk, hear the answer, keep going. Separate from the one-shot mic
  // button above, which only fills the box.
  const voice = useVoiceMode({ onUtterance: text => send(text) });
  const bottomRef = useRef();
  const inputRef = useRef();
  const recRef = useRef();

  // Shares the SAME conversation store as the mobile Ask AI, so it's one thread.
  const convKey = `titan_askai_${worker?.id || worker?.fullName || 'me'}`;

  useEffect(() => {
    let restored = false;
    try {
      const raw = localStorage.getItem(convKey);
      if (raw) { const saved = JSON.parse(raw); if (Array.isArray(saved) && saved.length) { setMessages(saved); restored = true; } }
    } catch {}
    loadContext(restored);
  }, [worker]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);
  useEffect(() => { if (messages.length) { try { localStorage.setItem(convKey, JSON.stringify(messages)); } catch {} } }, [messages, convKey]);

  async function loadContext(keepMessages) {
    setContextLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
      const getToken = () => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } };
      const apiFetch = path => fetch(`${API_BASE}${path}`, { headers: { 'Content-Type': 'application/json', ...(getToken() ? { 'x-worker-token': getToken() } : {}) } }).then(r => (r.ok ? r.json() : Promise.reject()));

      const [allCards, myProjects, checklists] = await Promise.all([
        getMemberCards(),
        apiFetch('/my-projects').catch(() => []),
        apiFetch('/checklists').catch(() => []),
      ]);
      const convResults = await Promise.all((myProjects || []).map(p => apiFetch(`/projects/${p.id}/conversations`).catch(() => [])));
      const conversations = [];
      (myProjects || []).forEach((p, i) => (convResults[i] || []).forEach(thread => conversations.push({ projectName: p.name, subJobName: thread.name, notes: thread.notes || [] })));

      const firstName = worker.firstName || worker.fullName?.split(' ')[0] || 'there';
      const fullName = worker.fullName || `${worker.firstName || ''} ${worker.lastName || ''}`.trim();
      const ctx = { cards: allCards, workerName: fullName, boardName: 'Titan Electric', conversations, checklists: Array.isArray(checklists) ? checklists : [] };
      setContext(ctx);
      const jobCount = allCards.length;
      const noteCount = conversations.reduce((s, t) => s + (t.notes?.length || 0), 0);
      if (!keepMessages) setMessages([{
        role: 'ai',
        text: jobCount > 0
          ? `Hey ${firstName}! I've loaded your ${jobCount} job${jobCount > 1 ? 's' : ''}${noteCount > 0 ? ` and ${noteCount} daily note${noteCount !== 1 ? 's' : ''} from your projects` : ''}. Ask me anything — what you need, where to be, what's left to do, or what's been discussed on your jobs.`
          : `Hey ${firstName}! I don't see any jobs assigned to you right now. Ask me anything, or check with the office if you're expecting work.`,
      }]);
    } catch {
      if (!keepMessages) setMessages([{ role: 'ai', text: 'Had trouble loading your jobs. Try refreshing.' }]);
    } finally { setContextLoading(false); }
  }

  async function send(text) {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const history = messages.filter(m => m.role !== 'system');
      const reply = await askAssistant(msg, context || { cards: [], workerName: worker?.fullName || '', boardName: '' }, history);
      setMessages(prev => [...prev, { role: 'ai', text: reply }]);
      voice.speak(reply);   // no-op unless hands-free is on
    } catch (e) {
      setMessages(prev => [...prev, { role: 'ai', text: `Sorry, I ran into an issue: ${e.message}. Try again in a moment.` }]);
    } finally { setLoading(false); setTimeout(() => inputRef.current?.focus(), 100); }
  }

  function newChat() {
    if (messages.length > 1 && !window.confirm('Start a new chat? This clears the current conversation.')) return;
    try { localStorage.removeItem(convKey); } catch {}
    setMessages([]);
    loadContext(false);
  }

  function startVoice() {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRec) { alert('Voice input not supported on this browser.'); return; }
    const rec = new SpeechRec();
    rec.onresult = e => { setInput(prev => (prev ? prev + ' ' : '') + Array.from(e.results).map(r => r[0].transcript).join(' ')); setListening(false); };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec; rec.start(); setListening(true);
  }

  const showSuggestions = messages.length <= 1 && !loading;

  return (
    <AdminLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 150px)', maxWidth: 900, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12, borderBottom: '1px solid var(--border, #e5e7eb)', marginBottom: 8 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="var(--orange, #e87722)" strokeWidth="1.2" fill="none" /><path d="M4 6h4M6 4v4" stroke="var(--orange, #e87722)" strokeWidth="1.2" strokeLinecap="round" /></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text, #111)' }}>Ask AI</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
              {contextLoading ? 'Loading your jobs…' : context ? `${context.cards.length} job${context.cards.length !== 1 ? 's' : ''} loaded · ${HAS_AI_KEY ? 'Live AI' : 'Demo'}` : 'Ready'}
            </div>
          </div>
          <button onClick={newChat} style={{ background: 'none', border: '1px solid var(--border, #e5e7eb)', color: 'var(--text-muted, #6b7280)', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '6px 12px', borderRadius: 16 }}>+ New chat</button>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, color: HAS_AI_KEY ? '#15803d' : '#9ca3af', background: HAS_AI_KEY ? '#dcfce7' : '#f3f4f6', borderRadius: 999, padding: '4px 10px' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: HAS_AI_KEY ? '#16a34a' : '#9ca3af' }} />{HAS_AI_KEY ? 'Live' : 'Demo'}
          </span>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 4px' }}>
          {contextLoading && <div style={{ color: 'var(--text-muted, #6b7280)', fontSize: 14, padding: '20px 0' }}>Loading your job data…</div>}
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
              <div style={{ maxWidth: '76%', padding: '11px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5,
                background: m.role === 'user' ? '#1e3a5f' : '#fff', color: m.role === 'user' ? '#fff' : '#1f2937',
                border: m.role === 'user' ? 'none' : '1px solid #eef2f7', boxShadow: m.role === 'user' ? 'none' : '0 1px 2px rgba(0,0,0,0.04)' }}>
                {m.role === 'ai' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, color: 'var(--orange, #e87722)', marginBottom: 5 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="var(--orange, #e87722)" strokeWidth="1.2" fill="none" /><path d="M4 6h4M6 4v4" stroke="var(--orange, #e87722)" strokeWidth="1.2" strokeLinecap="round" /></svg>
                    Titan AI
                  </div>
                )}
                <MessageText text={m.text} />
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
              <div style={{ padding: '11px 14px', borderRadius: 12, background: '#fff', border: '1px solid #eef2f7', color: '#9ca3af', fontSize: 14 }}>Titan AI is thinking…</div>
            </div>
          )}
          {showSuggestions && !contextLoading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10, marginTop: 10 }}>
              {SUGGESTED.map(s => (
                <button key={s.text} onClick={() => send(s.text)} style={{ display: 'flex', alignItems: 'center', gap: 9, textAlign: 'left', padding: '11px 13px', borderRadius: 10, border: '1px solid #eef2f7', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151' }}>
                  <span style={{ fontSize: 16 }}>{s.icon}</span>{s.text}
                </button>
              ))}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Hands-free banner */}
        {voice.active && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginTop: 10, borderRadius: 12, background: voice.speaking ? '#eff6ff' : '#f0fdf4', border: `1.5px solid ${voice.speaking ? '#bfdbfe' : '#bbf7d0'}` }}>
            <span style={{ fontSize: 18 }}>{voice.speaking ? '🔊' : '🎤'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: voice.speaking ? '#1d4ed8' : '#15803d' }}>
                {voice.speaking ? 'Speaking…' : voice.listening ? 'Listening — go ahead' : 'Ready'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {voice.heard || (voice.speaking ? 'Tap Stop to interrupt' : 'Just talk — it sends when you pause')}
              </div>
            </div>
            <button onClick={voice.stop}
              style={{ flexShrink: 0, padding: '8px 16px', borderRadius: 999, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>Stop</button>
          </div>
        )}

        {/* Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 12, borderTop: '1px solid var(--border, #e5e7eb)' }}>
          {voice.supported && !voice.active && (
            <button onClick={voice.start} title="Hands-free — talk and it talks back" aria-label="Hands-free voice mode"
              style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--orange, #e87722)', background: '#fff', color: 'var(--orange, #e87722)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>🎧</button>
          )}
          <button onClick={listening ? () => { recRef.current?.stop(); setListening(false); } : startVoice} aria-label="Voice input"
            style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--border, #e5e7eb)', background: listening ? '#fee2e2' : '#fff', color: listening ? '#dc2626' : '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="6" y="1" width="6" height="9" rx="3" stroke="currentColor" strokeWidth="1.4" fill={listening ? 'currentColor' : 'none'} /><path d="M2 9c0 3.9 3.1 7 7 7s7-3.1 7-7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" /><path d="M9 16v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
          </button>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} placeholder={contextLoading ? 'Loading jobs…' : 'Ask about your jobs…'}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()} disabled={loading || contextLoading}
            style={{ flex: 1, height: 44, padding: '0 16px', borderRadius: 22, border: '1px solid var(--border, #e5e7eb)', fontSize: 14, outline: 'none' }} />
          <button onClick={() => send()} disabled={!input.trim() || loading || contextLoading} aria-label="Send"
            style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', border: 'none', background: input.trim() && !loading ? 'var(--orange, #e87722)' : '#e5e7eb', color: '#fff', cursor: input.trim() && !loading ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 9h14M10 3l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
